import { Archive, ArchiveRestore, ArrowLeft, BadgeCheck, RefreshCw, Send, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import useSWR from "swr";
import { toast } from "sonner";
import { ORDER_DELETE_CONFIRM_PHRASE, type OrderRecord } from "@/shared/contracts";
import { apiFetch, jsonBody, swrFetcher } from "@/web/api";
import { CopyButton } from "@/web/components/copy-button";
import { PageHeader } from "@/web/components/page-header";
import { StatusBadge } from "@/web/components/status-badge";
import { Badge } from "@/web/components/ui/badge";
import { Button } from "@/web/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/web/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/web/components/ui/dialog";
import { Input } from "@/web/components/ui/input";
import { Label } from "@/web/components/ui/label";
import { Loading } from "@/web/components/ui/loading";
import { formatDate, formatMoney } from "@/web/lib/utils";

interface DetailResponse {
  order: OrderRecord;
  payment_events: Array<Record<string, unknown>>;
  notifications: Array<Record<string, unknown> & { attempts_detail?: Array<Record<string, unknown>> }>;
}

function Field({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return <div><dt className="text-xs text-muted">{label}</dt><dd className={`mt-1 break-all text-sm ${mono ? "font-mono text-xs" : ""}`}>{value || "—"}</dd></div>;
}

export function OrderDetailPage() {
  const { id = "" } = useParams();
  const { data, isLoading, mutate } = useSWR<DetailResponse>(`/admin-api/orders/${id}`, swrFetcher, { refreshInterval: 5_000 });
  const [repairOpen, setRepairOpen] = useState(false);
  const [repairBusy, setRepairBusy] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  // 删除已支付订单时必须回填平台订单号（后端也校验，这里做前端防呆）
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const navigate = useNavigate();

  async function doDelete() {
    setDeleteBusy(true);
    try {
      // 已支付订单要带上回填的平台订单号，后端据此放行（也是唯一的删除凭证）
      const suffix = paid ? `?confirm=${encodeURIComponent(deleteConfirmText.trim())}` : "";
      await apiFetch(`/admin-api/orders/${id}${suffix}`, { method: "DELETE" });
      toast.success("订单已删除");
      navigate("/orders");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "删除失败");
      setDeleteBusy(false);
    }
  }

  // 已支付订单只能归档，不能硬删（对账凭证）
  async function doArchive() {
    setDeleteBusy(true);
    try {
      await apiFetch(`/admin-api/orders/${id}/archive`, { method: "POST" });
      toast.success("订单已归档（记录仍保留）");
      await mutate();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "归档失败");
    } finally {
      setDeleteBusy(false);
    }
  }

  async function doUnarchive() {
    setDeleteBusy(true);
    try {
      await apiFetch(`/admin-api/orders/${id}/unarchive`, { method: "POST" });
      toast.success("已取消归档");
      await mutate();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "取消归档失败");
    } finally {
      setDeleteBusy(false);
    }
  }

  async function doRepair() {
    setRepairBusy(true);
    try {
      await apiFetch(`/admin-api/orders/${id}/mark-paid`, { method: "POST", ...jsonBody({}) });
      toast.success("补单成功，已触发商户通知");
      setRepairOpen(false);
      await mutate();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "补单失败");
    } finally {
      setRepairBusy(false);
    }
  }
  if (isLoading || !data) return <Loading label="正在读取订单" />;
  const order = data.order;
  const paid = order.status === "paid" || order.status === "late_paid";

  return (
    <>
      <div className="mb-4"><Button asChild variant="ghost" size="sm"><Link to="/orders"><ArrowLeft />返回订单列表</Link></Button></div>
      <PageHeader
        title={order.name}
        description={`商户订单号 ${order.out_trade_no}`}
        actions={
          <>
            <Button variant="outline" onClick={async () => { await apiFetch("/admin-api/scans/run", { method: "POST" }); toast.success("扫描已完成"); await mutate(); }}><RefreshCw />立即扫描</Button>
            <Button disabled={!paid} onClick={async () => { await apiFetch(`/admin-api/orders/${id}/resend`, { method: "POST" }); toast.success("已加入一次性补发队列"); await mutate(); }}><Send />补发通知</Button>
            <Button disabled={paid} onClick={() => setRepairOpen(true)}><BadgeCheck />补单</Button>
            {order.archived_at ? (
              <Button variant="outline" disabled={deleteBusy} onClick={doUnarchive}><ArchiveRestore />取消归档</Button>
            ) : paid ? (
              <Button variant="outline" disabled={deleteBusy} title="记录保留，仅从列表收起" onClick={doArchive}><Archive />归档</Button>
            ) : null}
            <Button variant="ghost" className="text-destructive hover:bg-destructive/10" onClick={() => { setDeleteConfirmText(""); setDeleteOpen(true); }}><Trash2 />删除</Button>
          </>
        }
      />

      <div className="grid gap-4 sm:grid-cols-3">
        <Card><CardContent className="pt-5"><div className="text-sm text-muted">商户金额</div><div className="mt-2 text-2xl font-semibold">{formatMoney(order.requested_amount_cents)}</div></CardContent></Card>
        <Card><CardContent className="pt-5"><div className="text-sm text-muted">要求实付</div><div className="mt-2 text-2xl font-semibold text-primary">{formatMoney(order.payable_amount_cents)}</div></CardContent></Card>
        <Card><CardContent className="pt-5"><div className="text-sm text-muted">订单状态</div><div className="mt-3"><StatusBadge status={order.status} /></div></CardContent></Card>
      </div>

      <div className="mt-6 grid gap-6 xl:grid-cols-2">
        <Card>
          <CardHeader><CardTitle>订单信息</CardTitle><CardDescription>订单过期后仍保留，自动监控在第 10 分钟结束。</CardDescription></CardHeader>
          <CardContent>
            <dl className="grid gap-5 sm:grid-cols-2">
              <Field label="平台订单号" value={order.trade_no} mono /><Field label="商户订单号" value={order.out_trade_no} mono />
              <Field label="协议版本" value={order.api_version.toUpperCase()} /><Field label="收款方式" value={order.collection_mode === "business_qr" ? "经营码金额匹配" : "转账备注匹配"} />
              <Field label="创建时间" value={formatDate(order.created_at)} /><Field label="收银台过期" value={formatDate(order.expires_at)} />
              <Field label="监控截止" value={formatDate(order.monitor_until)} /><Field label="支付时间" value={formatDate(order.paid_at)} />
              <Field label="支付宝账务流水号" value={order.alipay_account_log_id ?? ""} mono /><Field label="支付宝订单号" value={order.alipay_order_no ?? ""} mono />
              <Field label="回调地址" value={order.notify_url} mono /><Field label="同步返回地址" value={order.return_url ?? ""} mono />
            </dl>
            <div className="mt-5 flex gap-2"><CopyButton value={order.trade_no} label="复制平台单号" /><CopyButton value={order.out_trade_no} label="复制商户单号" /></div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>支付匹配</CardTitle><CardDescription>账务流水号全局去重，支出记录不会参与匹配。</CardDescription></CardHeader>
          <CardContent>
            {data.payment_events.length === 0 ? <div className="py-12 text-center text-sm text-muted">尚未匹配到账务流水</div> : data.payment_events.map((event) => (
              <div key={String(event.id)} className="rounded-md border p-4">
                <div className="flex items-center justify-between"><Badge variant="success">{String(event.direction)}</Badge><span className="font-mono text-sm">{formatMoney(Number(event.amount_cents))}</span></div>
                <dl className="mt-4 grid gap-4 sm:grid-cols-2"><Field label="账务流水号" value={String(event.account_log_id ?? "")} mono /><Field label="发生时间" value={formatDate(String(event.occurred_at ?? ""))} /><Field label="转账备注" value={String(event.trans_memo ?? "")} /><Field label="对方账号" value={String(event.other_account ?? "")} /></dl>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6">
        <CardHeader><CardTitle>通知投递</CardTitle><CardDescription>首次立即发送，失败后每 60 秒一次，自动任务最多 10 次；手动补发只执行一次。</CardDescription></CardHeader>
        <CardContent>
          {data.notifications.length === 0 ? <div className="py-12 text-center text-sm text-muted">支付成功后会在这里显示通知任务</div> : (
            <div className="space-y-4">
              {data.notifications.map((job) => (
                <div key={String(job.id)} className="rounded-md border p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2"><div className="flex items-center gap-2"><Badge variant={job.status === "succeeded" ? "success" : job.status === "failed" ? "danger" : "outline"}>{String(job.status)}</Badge>{Number(job.manual) ? <Badge variant="primary">手动补发</Badge> : null}</div><span className="text-xs text-muted">{String(job.attempts)} / {String(job.max_attempts)} 次</span></div>
                  <div className="mt-3 space-y-2">
                    {job.attempts_detail?.map((attempt) => (
                      <div key={String(attempt.id)} className="grid gap-1 border-t pt-2 text-xs sm:grid-cols-[80px_1fr_auto]"><span>第 {String(attempt.attempt_number)} 次</span><span className={attempt.error ? "text-destructive" : "text-muted"}>{String(attempt.error ?? attempt.response_body ?? "等待结果")}</span><span className="text-muted">{formatDate(String(attempt.completed_at ?? attempt.requested_at ?? ""))}</span></div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={deleteOpen} onOpenChange={(open) => { if (!open && !deleteBusy) { setDeleteOpen(false); setDeleteConfirmText(""); } }}>
        <DialogContent>
          <DialogTitle>确认删除订单</DialogTitle>
          <DialogDescription>
            将永久删除订单 {order.out_trade_no}，此操作不可恢复。
          </DialogDescription>
          {paid ? (
            <div className="mt-3 space-y-2">
              <p className="rounded-lg bg-destructive/[0.06] px-3.5 py-3 text-xs leading-5 text-destructive">
                该订单已支付成功。删除后交易记录、支付流水关联和对账依据会一并丢失。
                如果只是想让它从列表消失，请改用「归档」—— 记录会完整保留。
              </p>
              <Label htmlFor="detail-delete-confirm">请输入「{ORDER_DELETE_CONFIRM_PHRASE}」以确认</Label>
              <Input
                id="detail-delete-confirm"
                value={deleteConfirmText}
                onChange={(event) => setDeleteConfirmText(event.target.value.trim())}
                placeholder={ORDER_DELETE_CONFIRM_PHRASE}
                autoComplete="off"
                spellCheck={false}
                autoFocus
              />
            </div>
          ) : (
            <p className="mt-3 text-xs leading-5 text-muted">
              与该订单关联的金额预留、通知任务和投递记录会一并删除。
            </p>
          )}
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="ghost" disabled={deleteBusy} onClick={() => { setDeleteOpen(false); setDeleteConfirmText(""); }}>取消</Button>
            <Button
              variant="danger"
              disabled={deleteBusy || (paid && deleteConfirmText.trim() !== ORDER_DELETE_CONFIRM_PHRASE)}
              onClick={doDelete}
            >
              <Trash2 />{deleteBusy ? "正在删除" : "确认删除"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={repairOpen} onOpenChange={(open) => { if (!open && !repairBusy) setRepairOpen(false); }}>
        <DialogContent>
          <DialogTitle>确认补单</DialogTitle>
          <DialogDescription>
            将把订单 {order.out_trade_no} 标记为「支付成功」，并立即向商户发送支付通知。
          </DialogDescription>
          <p className="mt-3 text-xs leading-5 text-muted">
            仅在你已确认收到这笔款项后操作。补单不会产生支付宝流水记录，订单状态会变为已支付，商户侧将收到正常的异步通知。
          </p>
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="ghost" disabled={repairBusy} onClick={() => setRepairOpen(false)}>取消</Button>
            <Button disabled={repairBusy} onClick={doRepair}><BadgeCheck />{repairBusy ? "正在补单" : "确认补单"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
